// Home: where the customer stands at a glance. A cover hero, the things they
// came to do (report, upload, add kit, chat), anything waiting on them, what
// to do next (GET /me/next), and their numbers.
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, ChevronRight, FileUp, MessageCircle, Plus, ShieldAlert, ShieldCheck, Sparkles, Wrench, type LucideIcon } from 'lucide-react';
import type { BusinessProfile, Need } from '@cover/core/api/types.ts';
import { certificateUrl } from '@cover/core/api/client.ts';
import { useBrand } from '@cover/core/brand.tsx';
import { useChat } from '../components/Chat.tsx';
import { cn } from '@cover/core/cn.ts';
import { CLAIM_STATUS_LABEL, daysUntil, date, firstName, money, plural } from '@cover/core/format.ts';
import { Badge, type Tone } from '@cover/core/ui/Badge.tsx';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { Load } from '@cover/core/ui/States.tsx';
import { useAssetSummary, useClaims, useDocuments, usePolicies, useProfile, useQuotes } from '../api.ts';
import { WhatsNext } from '../components/WhatsNext.tsx';
import { useSession } from '../session.tsx';

const LEVEL: Record<Need['level'], string> = {
  legal_requirement: 'Required by law',
  contract_requirement: 'Your contracts ask for it',
  strongly_suggested: 'Worth having',
  optional: 'Optional',
};

type Alert = { to: string; icon: LucideIcon; text: string; tone: Tone };

function greeting(now = new Date()) {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export function HomePage() {
  const { me } = useSession();
  const profile = useProfile();
  const policies = usePolicies();
  const claims = useClaims();
  const brand = useBrand();
  const name = firstName(me.contact?.displayName);

  // Home has its own greeting rather than PageTitle; keep the tab title and the h1 focus target.
  useEffect(() => {
    document.title = `Home · ${brand.name}`;
  }, [brand.name]);

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3 lg:mb-8">
        <div className="min-w-0 max-w-full">
          <p className="text-sm font-medium text-mid">{greeting()}</p>
          <h1 tabIndex={-1} className="truncate text-[28px] font-bold leading-tight tracking-tight focus:outline-none sm:text-4xl">
            {name ? `Hi ${name}` : 'Your cover'}
          </h1>
        </div>
      </div>
      <Load q={profile}>
        {(p) => {
          const policy = policies.data?.find((x) => x.status === 'active');
          const openClaims = (claims.data ?? []).filter((c) => c.status !== 'closed' && c.status !== 'draft');
          const alerts: Alert[] = [
            ...openClaims.map((c) => ({
              to: `/claims/${c.id}`,
              icon: ShieldAlert,
              text: c.status === 'awaiting_info' ? `Claim ${c.reference}: we need something from you` : `Claim ${c.reference} is ${CLAIM_STATUS_LABEL[c.status].toLowerCase()}`,
              tone: (c.status === 'awaiting_info' ? 'warn' : 'info') as Tone,
            })),
            ...(p.coverStatus.underInsuredPence
              ? [
                  {
                    to: '/equipment',
                    icon: Wrench,
                    text: policy ? `Your tools come to ${money(p.coverStatus.underInsuredPence)} more than your tools cover` : `None of your tools (${money(p.coverStatus.underInsuredPence)}) are covered yet`,
                    tone: 'warn' as Tone,
                  },
                ]
              : []),
          ];

          return (
            <div className="space-y-6">
              <CoverHero profile={p} policyName={policy ? (policy.productName ?? policy.product) : null} policyNumber={policy?.policyNumber ?? null} />

              <QuickActions />

              <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
                <div className="min-w-0 space-y-6">
                  {alerts.length > 0 && (
                    <section aria-labelledby="todo">
                      <h2 id="todo" className="mb-3 text-lg font-semibold">
                        Things to look at
                      </h2>
                      <ul className="space-y-2">
                        {alerts.map((a) => (
                          <li key={a.text}>
                            <Link to={a.to} className="flex min-h-14 items-center gap-3 rounded-2xl bg-white px-4 py-3 text-ink no-underline shadow-card ring-1 ring-line transition hover:-translate-y-px hover:no-underline">
                              <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', a.tone === 'warn' ? 'bg-warn-bg text-warn-fg' : 'bg-info-bg text-info-fg')}>
                                <a.icon className="h-5 w-5" aria-hidden />
                              </span>
                              <span className="flex-1 font-medium">{a.text}</span>
                              <ChevronRight className="h-4 w-4 text-muted" aria-hidden />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <WhatsNext />

                  {p.needs.length > 0 && <Needs needs={p.needs} />}
                </div>

                <aside className="space-y-4" aria-label="Your numbers">
                  <EquipmentTile />
                  <StatTile to="/claims" label="Open claims" value={String(openClaims.length)} note={openClaims.length ? `Latest ${openClaims[0]!.reference}` : 'Nothing open'} />
                  <DocumentsTile />
                  {policy?.renewalDate && <StatTile to="/policies" label="Renewal" value={date(policy.renewalDate)} note={renewalNote(policy.renewalDate)} />}
                </aside>
              </div>
            </div>
          );
        }}
      </Load>
    </>
  );
}

function renewalNote(day: string) {
  const d = daysUntil(day);
  return d < 0 ? 'Past due' : d === 0 ? 'Today' : `In ${plural(d, 'day')}`;
}

const HERO: Record<BusinessProfile['coverStatus']['state'], { title: string; icon: LucideIcon }> = {
  covered: { title: 'You’re covered', icon: ShieldCheck },
  gaps: { title: 'You’re covered, with something to check', icon: ShieldCheck },
  renewal_due: { title: 'Your renewal is coming up', icon: ShieldCheck },
  no_cover: { title: 'Let’s get you covered', icon: ShieldAlert },
};

function CoverHero({ profile: p, policyName, policyNumber }: { profile: BusinessProfile; policyName: string | null; policyNumber: string | null }) {
  const quotes = useQuotes();
  const chat = useChat();
  const hasQuotes = !!quotes.data?.length;
  const h = HERO[p.coverStatus.state];
  const renewal = p.coverStatus.nextRenewal;
  return (
    <section
      aria-labelledby="cover-status"
      className="relative overflow-hidden rounded-3xl bg-brand p-6 text-on-brand sm:p-8"
      style={{ backgroundImage: 'radial-gradient(120% 120% at 100% 0%, rgb(255 255 255 / 0.22) 0%, transparent 55%), linear-gradient(135deg, transparent 40%, rgb(0 0 0 / 0.18) 100%)' }}
    >
      <div aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
      <div aria-hidden className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-white/5" />
      <div className="relative flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0 space-y-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">
            <h.icon className="h-3.5 w-3.5" aria-hidden />
            {p.name ?? 'Your business'}
          </span>
          <h2 id="cover-status" className="text-2xl font-bold tracking-tight sm:text-3xl">
            {h.title}
          </h2>
          {policyName && (
            <p className="text-on-brand/85">
              {policyName}
              {policyNumber && <span className="ml-2 font-mono text-sm text-on-brand/70">{policyNumber}</span>}
            </p>
          )}
          {renewal && <p className="text-sm text-on-brand/85">Renews {date(renewal)} · {renewalNote(renewal).toLowerCase()}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {policyNumber ? (
            <>
              <Link to="/policies" className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-white px-4 font-semibold text-brand no-underline hover:no-underline hover:brightness-95">
                See your policy <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <a href={certificateUrl(policyNumber)} target="_blank" rel="noopener" className="inline-flex min-h-11 items-center rounded-xl bg-white/15 px-4 font-semibold text-on-brand no-underline hover:bg-white/25 hover:no-underline">
                Certificate
              </a>
            </>
          ) : hasQuotes ? (
            <Link to="/quotes" className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-white px-4 font-semibold text-brand no-underline hover:no-underline hover:brightness-95">
              See your quotes <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          ) : (
            <button type="button" onClick={() => chat.open('I’d like a price for my cover')} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-white px-4 font-semibold text-brand hover:brightness-95">
              Get a price <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function QuickActions() {
  const chat = useChat();
  const actions: { to?: string; onClick?: () => void; icon: LucideIcon; label: string; hint: string }[] = [
    { to: '/claims/new', icon: ShieldAlert, label: 'Report something', hint: 'Theft, damage, an accident' },
    { to: '/documents', icon: FileUp, label: 'Send a document', hint: 'Receipts, schedules, photos' },
    { to: '/equipment', icon: Plus, label: 'Add equipment', hint: 'Keep your tools list current' },
    { onClick: () => chat.open(), icon: MessageCircle, label: 'Chat with us', hint: 'Ask anything, any time' },
  ];
  const cls = 'group flex h-full flex-col gap-3 rounded-2xl bg-white p-4 text-ink no-underline shadow-card ring-1 ring-line transition hover:-translate-y-0.5 hover:no-underline hover:ring-brand/30';
  const inner = (a: (typeof actions)[number]) => (
    <>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand transition group-hover:bg-brand group-hover:text-on-brand">
        <a.icon className="h-5 w-5" aria-hidden />
      </span>
      <span>
        <span className="block font-semibold">{a.label}</span>
        <span className="block text-sm text-mid">{a.hint}</span>
      </span>
    </>
  );
  return (
    <nav aria-label="Quick actions">
      <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {actions.map((a) => (
          <li key={a.label}>
            {a.to ? (
              <Link to={a.to} className={cls}>
                {inner(a)}
              </Link>
            ) : (
              <button type="button" onClick={a.onClick} className={cn(cls, 'w-full text-left')}>
                {inner(a)}
              </button>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

function StatTile({ to, label, value, note, children }: { to: string; label: string; value: string; note?: string; children?: ReactNode }) {
  return (
    <Link to={to} className="block rounded-2xl bg-white p-5 text-ink no-underline shadow-card ring-1 ring-line transition hover:-translate-y-px hover:no-underline">
      <p className="text-sm font-medium text-mid">{label}</p>
      <p className="mt-1 text-2xl font-bold tracking-tight tabular-nums">{value}</p>
      {children}
      {note && <p className="mt-1 text-sm text-mid">{note}</p>}
    </Link>
  );
}

function EquipmentTile() {
  const s = useAssetSummary();
  if (!s.data) return null;
  const { totalPence, coverLimitPence: limit } = s.data;
  const over = limit != null && totalPence > limit;
  const pct = limit ? Math.min(100, Math.round((totalPence / limit) * 100)) : 0;
  return (
    <StatTile to="/equipment" label="Your equipment" value={money(totalPence)} note={limit != null ? `${over ? 'Over' : 'Within'} your ${money(limit)} tools cover` : 'No tools cover yet'}>
      {limit != null && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-surface" role="img" aria-label={`${money(totalPence)} of a ${money(limit)} tools limit`}>
          <div className={over ? 'h-full rounded-full bg-warn-fg' : 'h-full rounded-full bg-brand'} style={{ width: `${over ? 100 : pct}%` }} />
        </div>
      )}
    </StatTile>
  );
}

function DocumentsTile() {
  const docs = useDocuments();
  if (!docs.data) return null;
  const checking = docs.data.filter((d) => d.status === 'needs_review').length;
  return <StatTile to="/documents" label="Documents" value={String(docs.data.length)} note={checking ? `${checking} being checked by our team` : 'All sorted'} />;
}

function Needs({ needs }: { needs: Need[] }) {
  const chat = useChat();
  return (
    <Card>
      <CardHeader title="Cover for a business like yours" description="Based on what you’ve told us. This is information, not advice." />
      <CardBody>
        <ul className="grid gap-3 sm:grid-cols-2">
          {needs.map((n) => (
            <li key={n.product} className={cn('rounded-2xl p-4 ring-1 ring-inset', n.coveredBy ? 'bg-ok-bg/30 ring-ok-fg/10' : 'bg-surface ring-line')}>
              <div className="mb-2 flex items-center justify-between gap-2">
                {n.coveredBy ? <CheckCircle2 className="h-5 w-5 text-ok-fg" aria-label="Covered" /> : <AlertTriangle className="h-5 w-5 text-warn-fg" aria-label="Not covered" />}
                <Badge tone={n.level === 'legal_requirement' ? 'brand' : 'neutral'}>{LEVEL[n.level]}</Badge>
              </div>
              <p className="font-semibold">{n.productName ?? n.product}</p>
              <p className="mt-0.5 text-sm text-mid">{n.reason}</p>
              <p className="mt-2 text-sm font-medium">
                {n.coveredBy ? `Covered${n.suggestedLimit ? ` · suggested limit ${n.suggestedLimit}` : ''}` : `Not covered yet${n.suggestedLimit ? ` · usual limit ${n.suggestedLimit}` : ''}`}
              </p>
            </li>
          ))}
        </ul>
        {(
          <p className="mt-4 flex items-center gap-1.5 text-sm text-mid">
            <Sparkles className="h-4 w-4 text-brand" aria-hidden />
            Questions about any of this?{' '}
            <button type="button" className="font-medium text-brand underline-offset-2 hover:underline" onClick={() => chat.open('I have a question about my cover: ')}>
              Ask us in the chat
            </button>
            .
          </p>
        )}
      </CardBody>
    </Card>
  );
}
